import {
  Alert,
  Button,
  CloseButton,
  Flex,
  Heading,
  IconPlusLine,
  Modal,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ReviewService } from "../../../../services/ReviewService";
import { TermsContext } from "../../../../utils/Context";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../../DialogYesNo/DialogYesNo";
import { ReviewCycleContext } from "../Review";
import CourseReviewCycleEntry from "./CourseReviewCycleEntry";
import CycleCreation from "./CycleCreation";
import { validateSequentialYears } from "./utils";

const CourseReviewCyclesModal = ({ open, setOpen, cycle }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { courseReviewCycles, setCourseReviewCycles } =
    useContext(ReviewCycleContext);
  const { academicYears } = useContext(TermsContext);
  const [upsertCycle, setUpsertCycle] = useState(!!cycle);
  const [editingCycle, setEditingCycle] = useState({
    id: cycle?.id || "",
    duration: cycle?.duration || 1,
    startYear: cycle?.start_date || {},
    courses: cycle?.reviewables || [],
    reflectionTemplateId: cycle?.reflection_template_id || "-1",
    notify_faculty_prior_term_end_days:
      cycle?.notify_faculty_prior_term_end_days,
    notify_faculty_past_term_end_days: cycle?.notify_faculty_past_term_end_days,
  });

  const [confirm, setConfirm] = useState(false);

  function verifyFields(duration, startYear, selectedCourses) {
    if (!duration || !startYear.id) {
      showNotification(t("courseReviewCycle.requiredFields"), ERROR);
      return false;
    } else if (selectedCourses.length === 0) {
      showNotification(t("courseReviewCycle.coursesRequired"), ERROR);
      return false;
    }

    const { valid } = validateSequentialYears(
      startYear,
      duration,
      academicYears
    );
    if (!valid) {
      showNotification(t("courseReviewCycle.sequentialYearsError"), ERROR);
      return false;
    }

    return true;
  }

  async function handleUpsertCycle(
    cycleId,
    duration,
    startYear,
    selectedCourses,
    reflectionTemplateId,
    notify_faculty_prior_term_end_days,
    notify_faculty_past_term_end_days
  ) {
    try {
      const cyclePayload = {
        start_date: startYear,
        duration: duration,
        notify_faculty_prior_term_end_days: notify_faculty_prior_term_end_days,
        notify_faculty_past_term_end_days: notify_faculty_past_term_end_days,
      };
      if (cycleId) {
        cyclePayload.id = cycleId;
      }
      const courseIds = selectedCourses.map((c) => c.id);

      const newCycle = await ReviewService.createOrUpdateReviewCycle(
        cyclePayload,
        courseIds,
        reflectionTemplateId
      );

      newCycle.reflection_template_id = reflectionTemplateId;
      newCycle.start_date = startYear;
      newCycle.reviewables = selectedCourses;
      newCycle.notify_faculty_prior_term_end_days =
        notify_faculty_prior_term_end_days;
      newCycle.notify_faculty_past_term_end_days =
        notify_faculty_past_term_end_days;

      if (cycleId) {
        setCourseReviewCycles((prevCycles) =>
          prevCycles.map((c) => (c.id === newCycle.id ? newCycle : c))
        );
        showNotification(
          t("notifications.general.updateSuccess", {
            obj: t("courseReviewCycle.courseReviewCycle"),
          })
        );
      } else {
        setCourseReviewCycles((prevCycles) => [...prevCycles, newCycle]);
        showNotification(
          t("notifications.general.createSuccess", {
            obj: t("courseReviewCycle.courseReviewCycle"),
          })
        );
      }
      if (cycle) {
        setOpen(false);
      } else {
        setUpsertCycle(false);
        setEditingCycle({
          id: "",
          duration: "",
          startYear: {},
          courses: [],
          reflectionTemplateId: "-1",
        });
      }
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("courseReviewCycle.courseReviewCycle"),
        }),
        ERROR
      );
    }
  }

  return (
    <Modal
      label={t("courseReviewCycle.manageCycles")}
      open={open}
      onClose={() => setOpen(false)}
      size="large"
      onExited={() => {
        setUpsertCycle(!!cycle);
        setEditingCycle({
          id: cycle?.id || "",
          duration: cycle?.duration || 1,
          startYear: cycle?.start_date || {},
          courses: cycle?.reviewables || [],
          reflectionTemplateId: cycle?.reflection_template_id || "-1",
          notify_faculty_prior_term_end_days:
            cycle?.notify_faculty_prior_term_end_days,
          notify_faculty_past_term_end_days:
            cycle?.notify_faculty_past_term_end_days,
        });
      }}
    >
      <Modal.Header>
        <Heading level="h2" as="h1">
          {t("courseReviewCycle.manageCycles")}
        </Heading>
        <CloseButton
          placement="end"
          offset="medium"
          screenReaderLabel={t("global.close")}
          onClick={() => setOpen(false)}
        />
      </Modal.Header>
      <Modal.Body>
        {upsertCycle ? (
          <CycleCreation
            editingCycle={editingCycle}
            setEditingCycle={setEditingCycle}
          />
        ) : (
          <Flex wrap="wrap" gap="small">
            <Flex.Item shouldGrow textAlign="end">
              <Button
                color="primary"
                renderIcon={IconPlusLine}
                onClick={() => setUpsertCycle(true)}
              >
                {t("cyclesAndGoals.addButton")}
              </Button>
            </Flex.Item>
            {courseReviewCycles ? (
              courseReviewCycles.length > 0 ? (
                courseReviewCycles.map((cycle) => (
                  <Flex.Item key={cycle.id} size="100%">
                    <CourseReviewCycleEntry
                      cycle={cycle}
                      setUpsertCycle={setUpsertCycle}
                      setEditingCycle={setEditingCycle}
                    />
                  </Flex.Item>
                ))
              ) : (
                <Flex.Item size="100%">
                  <Alert>{t("courseReviewCycle.noCyclesYet")}</Alert>
                </Flex.Item>
              )
            ) : (
              <>
                <Flex.Item size="100%">
                  <Skeleton height={"3.5rem"} />
                </Flex.Item>
                <Flex.Item size="100%">
                  <Skeleton height={"3.5rem"} />
                </Flex.Item>
                <Flex.Item size="100%">
                  <Skeleton height={"3.5rem"} />
                </Flex.Item>
              </>
            )}
          </Flex>
        )}
      </Modal.Body>
      <Modal.Footer>
        {upsertCycle ? (
          <>
            <Button
              margin="0 small 0 0"
              onClick={() => {
                if (cycle) {
                  setOpen(false);
                } else {
                  setUpsertCycle(false);
                  setEditingCycle({
                    id: cycle?.id || "",
                    duration: cycle?.duration || 1,
                    startYear: cycle?.start_date || {},
                    courses: cycle?.reviewables || [],
                    reflectionTemplateId: cycle?.reflection_template_id || "-1",
                    notify_faculty_prior_term_end_days:
                      cycle?.notify_faculty_prior_term_end_days,
                    notify_faculty_past_term_end_days:
                      cycle?.notify_faculty_past_term_end_days,
                  });
                }
              }}
            >
              {t("global.cancel")}
            </Button>
            <Button
              color="primary"
              onClick={() => {
                if (
                  verifyFields(
                    editingCycle.duration,
                    editingCycle.startYear,
                    editingCycle.courses
                  )
                ) {
                  setConfirm(true);
                }
              }}
            >
              {editingCycle.id
                ? t("courseReviewCycle.updateCycle")
                : t("courseReviewCycle.createCycle")}
            </Button>
          </>
        ) : (
          <Button onClick={() => setOpen(false)}>{t("global.close")}</Button>
        )}
        <DialogYesNo
          message={t("courseReviewCycle.upsertConfirm")}
          openStatus={confirm}
          onClickNo={() => setConfirm(false)}
          onClickYes={async () => {
            await handleUpsertCycle(
              editingCycle.id,
              editingCycle.duration,
              editingCycle.startYear,
              editingCycle.courses,
              editingCycle.reflectionTemplateId,
              editingCycle.notify_faculty_prior_term_end_days,
              editingCycle.notify_faculty_past_term_end_days
            );
            setConfirm(false);
          }}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default CourseReviewCyclesModal;
